/**
 * FormCard — the section primitive every settings form composes.
 *
 * Mirrors `prototype/settings/settings-company.html ▸ .form-card`:
 *   - Header: icon chip · title + lead description · optional badge slot.
 *   - Body: the form fields (caller renders).
 *
 * `tone="danger"` swaps the header bg + icon background for the rose
 * palette — used by the "Danger zone" section on settings pages with
 * destructive actions. We don't render the danger content here; callers
 * compose their own row layout inside the body.
 */
import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn, Card, CardBody } from "@flint/ui";

export interface FormCardProps {
  /** Icon shown in the chip on the left of the header. */
  icon: React.ReactNode;
  /** Section title (h2). */
  title: string;
  /** Lead paragraph under the title. */
  description?: React.ReactNode;
  /** Optional pill on the right of the header (e.g. "Visible to customers"). */
  badge?: React.ReactNode;
  /** Tone — `danger` swaps to the rose-tinted Danger Zone shell. */
  tone?: "default" | "danger";
  children: React.ReactNode;
  /** Anchor id so jump-links from the rail / TOC can scroll to this card. */
  id?: string;
  /**
   * Override the body wrapper's classes (default `p-5`). Pass `"p-0"`
   * for surfaces — like the Numbering rows — where the inner content
   * needs to extend edge-to-edge inside the card. The caller is then
   * responsible for any per-row inset.
   */
  bodyClassName?: string;
  /**
   * When set, the header becomes a disclosure toggle and the body can be
   * collapsed. Used for roadmap/"Coming soon" sections that shouldn't take up
   * vertical space until the reader opts in.
   */
  collapsible?: boolean;
  /** With `collapsible`, start collapsed (body hidden) on first render. */
  defaultCollapsed?: boolean;
}

export function FormCard({
  icon,
  title,
  description,
  badge,
  tone = "default",
  children,
  id,
  bodyClassName = "p-5",
  collapsible = false,
  defaultCollapsed = false,
}: FormCardProps) {
  const isDanger = tone === "danger";
  const [open, setOpen] = React.useState(!defaultCollapsed);

  const headerInner = (
    <>
      <span
        aria-hidden
        className={cn(
          "inline-grid place-items-center h-8 w-8 rounded-sm shrink-0",
          isDanger ? "bg-bad-100 text-bad" : "bg-sky-50 text-sky-700",
        )}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-semibold text-ink leading-tight">{title}</h2>
        {description ? (
          <p className="mt-0.5 text-xs text-muted leading-relaxed">{description}</p>
        ) : null}
      </div>
      {badge ? <div className="shrink-0">{badge}</div> : null}
      {collapsible ? (
        <ChevronDown
          aria-hidden
          size={16}
          className={cn(
            "mt-0.5 shrink-0 text-muted transition-transform",
            open && "rotate-180",
          )}
        />
      ) : null}
    </>
  );

  const headerClasses = cn(
    "flex items-start gap-3 px-5 py-4 border-b border-line",
    isDanger && "bg-bad-50/60 border-b-bad-100",
  );

  return (
    <Card id={id} className={cn(isDanger ? "border-bad-100" : "")}>
      <CardBody className="p-0">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className={cn(headerClasses, "w-full text-left", !open && "border-b-0")}
          >
            {headerInner}
          </button>
        ) : (
          <header className={headerClasses}>{headerInner}</header>
        )}

        {!collapsible || open ? <div className={bodyClassName}>{children}</div> : null}
      </CardBody>
    </Card>
  );
}
